import type { Cover, OutlineData } from '~/design-system/components/OutlinePanel/types/outline-panel.types'

/**
 * Helpers for the canvas sandbox's goal/outline doc cards. The agent stores the goal as markdown
 * and the outline as `OutlineData` JSON; these turn both into readable markdown for the full-screen
 * reader, plus a short plain-text teaser for the canvas card.
 */

/** Flatten the outline JSON (cover + chapters + canvases) into a readable markdown document. */
export function outlineToMarkdown(content: string | undefined): string {
  if (!content || !content.trim()) return ''
  let data: OutlineData
  try {
    data = JSON.parse(content) as OutlineData
  } catch {
    // Not JSON — assume it's already markdown/plain text.
    return content
  }
  if (!data || !Array.isArray(data.chapters)) return content

  const lines: string[] = []
  if (data.cover?.title) {
    lines.push(`# ${data.cover.title}`)
    if (data.cover.subtitle) lines.push('', data.cover.subtitle)
    lines.push('')
  }
  for (const chapter of data.chapters) {
    const heading = chapter.number ? `${chapter.number}. ${chapter.title}` : chapter.title
    lines.push(`## ${heading}`)
    if (chapter.description) lines.push('', chapter.description)
    for (const canvas of chapter.canvases ?? []) {
      lines.push('', `### ${canvas.title}`)
      if (canvas.visualDescription) lines.push('', `*${canvas.visualDescription}*`)
      if (canvas.content) lines.push('', canvas.content)
    }
    lines.push('')
  }
  return lines.join('\n').trim()
}

/**
 * The outline's OPENER canvas id (mirrors the backend's `OutlineCanvasExtractor.COVER_CANVAS_ID`).
 *
 * The cover is the deck's first canvas, not a label on top of the outline: it gets a card, a document in
 * the library, and it is the source for slide 1. Outlines written before the cover carried an id fall back
 * to this constant on read, so an old deck grows its opener without being regenerated.
 */
export const COVER_CANVAS_ID = 'cover'

/** True when `canvasId` addresses the outline's opener — its own id, or the default for a cover without one. */
function isCoverCanvasId(cover: Cover | undefined, canvasId: string): boolean {
  const own = cover?.id?.trim()
  return own ? own === canvasId : canvasId === COVER_CANVAS_ID
}

/** The opener's body: `cover.content` is the truth; `subtitle` is the legacy one-liner that stands in. */
function coverBody(cover: Cover | undefined): string {
  const content = (cover?.content ?? '').trim()
  if (content) return content
  return (cover?.subtitle ?? '').trim()
}

/** One per-canvas document: its outline canvas `id`, a display `title`, and the editable markdown
 *  body (`# title` + content) that backs its canvas doc card + fullscreen reader. */
export interface CanvasDoc {
  id: string
  title: string
  content: string
}

/** Compose ONE outline canvas into the markdown a canvas doc shows + edits: a title heading followed by
 *  its content body. (The canvas's `visualDescription` is intentionally kept OUT of the editable body so
 *  the round-trip stays clean — it's preserved untouched in the OutlineData on save.) */
function canvasToDocMarkdown(title: string, content: string | undefined): string {
  const body = (content ?? '').trim()
  return body ? `# ${title}\n\n${body}` : `# ${title}`
}

/** Split the structured outline JSON into one document PER canvas (in reading order: chapter → canvas),
 *  each with a stable id (the canvas id), a title, and composed markdown. Empty when the content isn't
 *  outline JSON or has no canvases — the caller then seeds no per-canvas cards. */
export function outlineToCanvasDocs(content: string | undefined): CanvasDoc[] {
  if (!content || !content.trim()) return []
  let data: OutlineData
  try {
    data = JSON.parse(content) as OutlineData
  } catch {
    return []
  }
  if (!data || !Array.isArray(data.chapters)) return []
  const docs: CanvasDoc[] = []
  // The OPENER leads, before chapter 1. Without it the deck's first slide had no source document and
  // generation fell back to wiring the Goal — the user's private brief standing in for what the audience
  // reads first. Title-only covers still produce a card, so the opener is always somewhere to write into.
  const coverTitle = data.cover?.title?.trim()
  if (coverTitle) {
    docs.push({
      id: data.cover?.id?.trim() || COVER_CANVAS_ID,
      title: coverTitle,
      content: canvasToDocMarkdown(coverTitle, coverBody(data.cover)),
    })
  }
  for (const chapter of data.chapters) {
    for (const canvas of chapter.canvases ?? []) {
      if (!canvas?.id) continue
      const title = canvas.title?.trim() || 'Untitled'
      docs.push({ id: canvas.id, title, content: canvasToDocMarkdown(title, canvas.content) })
    }
  }
  return docs
}

/** The editable reader markdown for ONE canvas (by id), composed from the live outline — `# title` +
 *  content. Empty string when the outline can't be parsed or the canvas is gone. */
export function findCanvasDocMarkdown(content: string | undefined, canvasId: string): string {
  if (!content) return ''
  let data: OutlineData
  try {
    data = JSON.parse(content) as OutlineData
  } catch {
    return ''
  }
  if (isCoverCanvasId(data.cover, canvasId)) {
    return canvasToDocMarkdown(data.cover?.title?.trim() || 'Untitled', coverBody(data.cover))
  }
  for (const chapter of data.chapters ?? []) {
    for (const canvas of chapter.canvases ?? []) {
      if (canvas?.id === canvasId) return canvasToDocMarkdown(canvas.title?.trim() || 'Untitled', canvas.content)
    }
  }
  return ''
}

/** Decompose a canvas doc's edited markdown back into `{ title, body }`: the first non-empty line (its
 *  leading heading/list marker stripped) is the title; everything after it is the body. Mirrors the
 *  title convention the rest of the doc system uses. */
function splitDocTitleAndBody(markdown: string): { title: string; body: string } {
  const lines = (markdown ?? '').split('\n')
  let i = 0
  while (i < lines.length && lines[i].trim().length === 0) i += 1
  const titleLine = i < lines.length ? lines[i] : ''
  const title = titleLine.replace(/^\s*#{1,6}\s+/, '').replace(/^\s*[-*+]\s+/, '').trim() || 'Untitled'
  // Body = everything after the title line, with one leading blank line dropped.
  let j = i + 1
  if (j < lines.length && lines[j].trim().length === 0) j += 1
  const body = lines.slice(j).join('\n').trim()
  return { title, body }
}

/** Write an edited canvas doc back into the structured outline: find the canvas by id, replace its
 *  `title` + `content` (preserving `visualDescription` + every other canvas/chapter), and return the
 *  updated OutlineData JSON string. Returns null when the outline can't be parsed, the canvas is gone,
 *  or nothing actually changed (the caller then skips the save). */
export function writeCanvasToOutline(
  content: string | undefined,
  canvasId: string,
  edited: string,
): string | null {
  if (!content) return null
  let data: OutlineData
  try {
    data = JSON.parse(content) as OutlineData
  } catch {
    return null
  }
  if (!Array.isArray(data.chapters)) return null
  const { title, body } = splitDocTitleAndBody(edited)
  let changed = false
  // Editing the opener card writes the cover, not a chapter canvas. The body lands in `content` (not
  // `subtitle`) so the next read gets back exactly what was typed — subtitle is left as the agent wrote it.
  if (isCoverCanvasId(data.cover, canvasId)) {
    if (!data.cover) return null
    if (data.cover.title !== title || coverBody(data.cover) !== body) {
      data.cover.id = data.cover.id?.trim() || COVER_CANVAS_ID
      data.cover.title = title
      data.cover.content = body
      changed = true
    }
    return changed ? JSON.stringify(data) : null
  }
  for (const chapter of data.chapters) {
    for (const canvas of chapter.canvases ?? []) {
      if (canvas?.id !== canvasId) continue
      if (canvas.title !== title || (canvas.content ?? '') !== body) {
        canvas.title = title
        canvas.content = body
        changed = true
      }
    }
  }
  return changed ? JSON.stringify(data) : null
}

/** A short, single-paragraph teaser for a card: strip markdown syntax and truncate. */
function toPlainPreview(markdown: string | undefined, max = 320): string {
  if (!markdown) return ''
  const text = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_>`#-]+/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
  if (text.length <= max) return text
  return `${text.slice(0, max).trimEnd()}…`
}

/** Count outline chapters for the card's secondary label, when the content is outline JSON. */
function countOutlineChapters(content: string | undefined): number {
  if (!content) return 0
  try {
    const data = JSON.parse(content) as OutlineData
    return Array.isArray(data.chapters) ? data.chapters.length : 0
  } catch {
    return 0
  }
}
